import type { ReactNode } from 'react';
import { Archive, Ban, Check, FilePlus2, Hourglass, Pause, Play, Printer } from 'lucide-react';

import type { DataViewActionSection, DataViewSelection } from 'components/DataView';
import { BundleState, TotadminPermission } from 'data/__generated__';

import { getBoxesToPrint, PrintBoxTags } from '../components/PrintBoxTags';

import type { BundleRow } from './bundlesColumns';
import { BUNDLE_STATES_LABELS, canTransitionBundleState } from './states';

// Ordered by the natural preparation lifecycle so the action bar reads top-to-bottom.
const STATE_ACTIONS: { state: BundleState; icon: ReactNode }[] = [
    { state: BundleState.Created, icon: <FilePlus2 /> },
    { state: BundleState.Started, icon: <Play /> },
    { state: BundleState.InProgress, icon: <Hourglass /> },
    { state: BundleState.Paused, icon: <Pause /> },
    { state: BundleState.Completed, icon: <Check /> },
    { state: BundleState.Aborted, icon: <Ban /> },
    { state: BundleState.Archived, icon: <Archive /> },
];

/**
 * La barre d'actions des bundles, en config. L'impression des étiquettes y entre : elle porte sur la
 * sélection, comme les transitions d'état — l'en-tête de page n'a plus à la connaître.
 */
export function buildBundleActions({
    onUpdateState,
    isUpdatingState,
}: {
    onUpdateState: (state: BundleState, selectedBundles: BundleRow[], selection: DataViewSelection) => void;
    isUpdatingState: boolean;
}): DataViewActionSection<BundleRow>[] {
    return [
        {
            title: 'Changer le statut',
            actions: STATE_ACTIONS.map(({ state, icon }) => ({
                key: `state-${state}`,
                requiredPermissions: [TotadminPermission.BundlesManage],
                icon,
                label: BUNDLE_STATES_LABELS[state] ?? state,
                // Une cible reste grise tant que TOUS les bundles sélectionnés ne peuvent pas l'atteindre,
                // selon les règles que le back applique dans `updateBundleState`.
                disabledWhen: (selectedBundles) =>
                    isUpdatingState ||
                    !selectedBundles.length ||
                    selectedBundles.some((bundle) => !canTransitionBundleState(bundle.state, state)),
                onClick: (selectedBundles, selection) => onUpdateState(state, selectedBundles, selection),
            })),
        },
        {
            title: 'Étiquettes',
            actions: [
                {
                    key: 'print-box-tags',
                    icon: <Printer />,
                    label: 'Imprimer les étiquettes',
                    disabledWhen: (selectedBundles) => !selectedBundles.length,
                    // Pas une popup : le composant monte les étiquettes cachées et ouvre le dialogue
                    // d'impression du navigateur quand l'action s'allume.
                    renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                        <PrintBoxTags
                            isOpen={isOpen}
                            setIsOpen={setIsOpen}
                            boxesToPrint={getBoxesToPrint(rows.filter(({ _id }) => selectedKeys.includes(_id)))}
                        />
                    ),
                },
            ],
        },
    ];
}
